/**
 * Bringing the members across.
 *
 * This screen is used once per club, by somebody who has never seen it
 * before, on the most consequential afternoon of the whole engagement.
 * So it is laid out as four numbered steps rather than as a form, and
 * every step says what will happen before it happens.
 *
 * ---------------------------------------------------------------------
 * THE TWO NUMBERS AT THE TOP
 *
 * How many memberships the club has, and what they owe between them,
 * are asked for BEFORE the file is chosen. That order is deliberate. If
 * the file is uploaded first, whoever is doing this reads the totals off
 * the screen and types them back in, and the check that catches a
 * quietly short export becomes a check that agrees with itself.
 *
 * Those numbers come from the old system's reports — a membership count
 * and an aged receivables total. If they do not match the file, the
 * import refuses, and this screen shows the difference rather than a
 * refusal somebody has to interpret.
 *
 * ---------------------------------------------------------------------
 * COMMIT IS NOT A SAVE BUTTON
 *
 * It creates hundreds of memberships and posts to the general ledger, it
 * cannot be undone by pressing anything, and the person doing it has
 * been reading a grid of four hundred rows for an hour. It is styled as
 * what it is, it states its consequences on the button's own line, and
 * it is disabled until the file and the club's own totals agree.
 */

import { useMemo, useState } from 'react';
import { api, getToken, ApiError } from '../lib/api.ts';
import { useApi, useMutation } from '../lib/useApi.ts';
import { useSession } from '../lib/session.tsx';
import { money, parseMoney, date as fmtDate } from '../lib/format.ts';
import { Card, Empty, ErrorBanner, Loading, Status } from '../components/ui.tsx';

interface ImportRow {
  row_number: number;
  membership_number: string | null;
  role: string | null;
  name: string;
  email: string | null;
  class_code: string | null;
  joined_on: string | null;
  date_of_birth: string | null;
  age: number | null;
  balance_minor: string | null;
  errors: string[];
  warnings: string[];
  has_error: boolean;
  imported: boolean;
}

interface Reconciliation {
  memberships_expected: number | null;
  memberships_found: number;
  memberships_missing: number | null;
  balance_expected_minor: number | null;
  balance_found_minor: number;
  balance_difference_minor: number | null;
}

interface Batch {
  id: string;
  source_filename: string | null;
  status: string;
  as_of_date: string;
  row_count: number;
  error_count: number;
  warning_count: number;
  membership_count: number;
  person_count: number;
  balance_minor: string;
  expected_memberships: number | null;
  expected_balance_minor: string | null;
  created_at: string;
  committed_at: string | null;
  journal_entry_id: string | null;
}

interface ImportDetail {
  import: Batch;
  reconciliation: Reconciliation;
  can_commit: boolean;
  rows: ImportRow[];
  truncated: boolean;
  ignored_columns?: string[];
  columns?: { field: string; heading: string }[];
}

interface Committed {
  memberships: number;
  people: number;
  balance_minor: number;
  journal_entry_id: string | null;
}

const ROLE_LABEL: Record<string, string> = {
  primary: 'Member',
  spouse: 'Spouse',
  dependent: 'Dependent',
  authorized_user: 'Authorized',
};

export function MemberImportPage() {
  const { currency, atLeast } = useSession();

  const [file, setFile] = useState<{ name: string; text: string } | null>(null);
  const [asOf, setAsOf] = useState(() => new Date().toISOString().slice(0, 10));
  const [expectedCount, setExpectedCount] = useState('');
  const [expectedBalance, setExpectedBalance] = useState('');

  const [detail, setDetail] = useState<ImportDetail | null>(null);
  const [committed, setCommitted] = useState<Committed | null>(null);
  const [show, setShow] = useState<'all' | 'errors'>('all');
  const [confirmed, setConfirmed] = useState(false);

  const history = useApi<{ imports: Batch[] }>(committed ? null : '/v1/member-imports');

  const stage = useMutation(async () =>
    api<ImportDetail>('/v1/member-imports', {
      method: 'POST',
      body: {
        content: file!.text,
        filename: file!.name,
        as_of_date: asOf,
        expected_memberships: expectedCount === '' ? undefined : Number(expectedCount),
        expected_balance_minor:
          expectedBalance === '' ? undefined : parseMoney(expectedBalance, currency) ?? undefined,
      },
    }),
  );

  const revalidate = useMutation(async (id: string) =>
    api<ImportDetail>(`/v1/member-imports/${id}/validate`, {
      method: 'POST',
      body: {
        expected_memberships: expectedCount === '' ? null : Number(expectedCount),
        expected_balance_minor:
          expectedBalance === '' ? null : parseMoney(expectedBalance, currency),
      },
    }),
  );

  const commit = useMutation(async (id: string) =>
    api<ImportDetail & { committed: Committed }>(`/v1/member-imports/${id}/commit`, {
      method: 'POST',
      body: { confirm: true },
    }),
  );

  const discard = useMutation(async (id: string) =>
    api<null>(`/v1/member-imports/${id}`, { method: 'DELETE' }),
  );

  async function downloadTemplate() {
    const res = await fetch('/v1/member-imports/template.csv', {
      headers: { authorization: `Bearer ${getToken()}` },
    });
    const url = URL.createObjectURL(await res.blob());
    const a = document.createElement('a');
    a.href = url;
    a.download = 'member-import-template.csv';
    a.click();
    URL.revokeObjectURL(url);
  }

  async function onFile(f: File) {
    setFile({ name: f.name, text: await f.text() });
    setDetail(null);
    setConfirmed(false);
  }

  async function doStage() {
    const r = await stage.run();
    if (r) {
      setDetail(r);
      setConfirmed(false);
    }
  }

  async function doRevalidate() {
    if (!detail) return;
    const r = await revalidate.run(detail.import.id);
    if (r) setDetail(r);
  }

  async function doCommit() {
    if (!detail) return;
    const r = await commit.run(detail.import.id);
    if (r) {
      setDetail(r);
      setCommitted(r.committed);
    }
  }

  async function doDiscard() {
    if (!detail) return;
    await discard.run(detail.import.id);
    setDetail(null);
    setFile(null);
    setConfirmed(false);
    history.reload();
  }

  const rows = useMemo(
    () => (detail?.rows ?? []).filter((r) => show === 'all' || r.has_error),
    [detail, show],
  );

  // ------------------------------------------------------------------
  // Afterwards
  // ------------------------------------------------------------------
  if (committed && detail) {
    return (
      <>
        <h1 style={{ marginBottom: 4 }}>The members are across</h1>
        <div className="ok-banner">
          <strong>
            {committed.memberships} membership{committed.memberships === 1 ? '' : 's'} and{' '}
            {committed.people} {committed.people === 1 ? 'person' : 'people'} imported.
          </strong>
          <div style={{ marginTop: 6 }}>
            Opening balances of {money(committed.balance_minor, currency)} were posted as at{' '}
            {fmtDate(detail.import.as_of_date)} — debit Accounts Receivable, credit{' '}
            <strong>3910 Opening Balances — Conversion</strong>. Agree that entry against your old
            system's trial balance, then clear 3910.
          </div>
          <div className="actions" style={{ marginTop: 12 }}>
            <a href="/members">
              <button>View the roster</button>
            </a>
            {committed.journal_entry_id && (
              <a href="/finance/trial-balance">
                <button className="secondary">Check the trial balance</button>
              </a>
            )}
          </div>
        </div>
      </>
    );
  }

  // ------------------------------------------------------------------
  return (
    <>
      <h1 style={{ marginBottom: 4 }}>Bring your members across</h1>
      <p className="muted small" style={{ marginTop: 0, marginBottom: 18 }}>
        One row per person. Rows that share a membership number are one household, so a family of
        four is four rows. Nothing is created until you commit.
      </p>

      <ErrorBanner error={stage.error} />
      <ErrorBanner error={revalidate.error} />
      <CommitError error={commit.error} />

      {/* ---------------------------------------------------------- */}
      <Card title="1 · What Your Old System Says You Have">
        <p className="muted small" style={{ marginTop: 0 }}>
          Take these from your current system's reports, before looking at the file. They are what
          catches an export that is quietly short — every row in one of those is perfectly valid,
          and there are simply fewer of them than you have members.
        </p>

        <div className="row" style={{ gap: 16, flexWrap: 'wrap' }}>
          <label className="field">
            <span>How many memberships?</span>
            <input
              type="number"
              min={0}
              value={expectedCount}
              onChange={(e) => setExpectedCount(e.target.value)}
              placeholder="412"
              style={{ maxWidth: 160 }}
              disabled={detail !== null}
            />
          </label>

          <label className="field">
            <span>What do they owe between them?</span>
            <input
              value={expectedBalance}
              onChange={(e) => setExpectedBalance(e.target.value)}
              placeholder="486,210.00"
              style={{ maxWidth: 200 }}
              disabled={detail !== null}
            />
            <div className="hint">Your aged receivables total. A credit balance counts against it.</div>
          </label>

          <label className="field">
            <span>Balances are as at</span>
            <input
              type="date"
              value={asOf}
              onChange={(e) => setAsOf(e.target.value)}
              style={{ maxWidth: 190 }}
              disabled={detail !== null}
            />
            <div className="hint">The cutover date. The journal entry is dated here.</div>
          </label>
        </div>
      </Card>

      {/* ---------------------------------------------------------- */}
      <Card title="2 · The File">
        <p className="muted small" style={{ marginTop: 0 }}>
          The sample below has a household already in it, which is the part people ask about. Your
          own column headings are usually recognized as they are — <em>Membership #</em>,{' '}
          <em>Last Name</em>, <em>Member Since</em>, <em>Balance Due</em> — so an export from your
          current system will often import without being rearranged.
        </p>

        <div className="actions" style={{ marginBottom: 14 }}>
          <button className="secondary small" onClick={() => void downloadTemplate()}>
            Download the sample file
          </button>
        </div>

        <label className="field">
          <span>Your roster</span>
          <input
            type="file"
            accept=".csv,text/csv"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) void onFile(f);
            }}
          />
        </label>

        {file && !detail && (
          <>
            <div className="muted small">
              {file.name} · {file.text.split('\n').filter((l) => l.trim() !== '').length - 1} rows
            </div>
            <div className="actions" style={{ marginTop: 12 }}>
              <button onClick={doStage} disabled={stage.busy}>
                {stage.busy ? 'Reading…' : 'Check this file'}
              </button>
              <span className="muted small">This reads the file. It does not import anything.</span>
            </div>
          </>
        )}
      </Card>

      {/* ---------------------------------------------------------- */}
      {detail && (
        <>
          <Card title="3 · What Is in It">
            <Reconcile
              rec={detail.reconciliation}
              batch={detail.import}
              currency={currency}
            />

            {detail.ignored_columns && detail.ignored_columns.length > 0 && (
              <div className="hint" style={{ marginTop: 12 }}>
                Columns kept but not imported: {detail.ignored_columns.join(', ')}. Nothing was
                thrown away — they are stored with each row.
              </div>
            )}

            <div className="actions" style={{ marginTop: 14 }}>
              <button className="secondary small" onClick={doRevalidate} disabled={revalidate.busy}>
                {revalidate.busy ? 'Checking…' : 'Check it again'}
              </button>
              <button className="secondary small" onClick={doDiscard} disabled={discard.busy}>
                Start over with a different file
              </button>
            </div>
            <div className="hint" style={{ marginTop: 8 }}>
              Edit the spreadsheet and start over, or correct the two numbers above and check again.
            </div>
          </Card>

          <Card
            title={`${detail.import.row_count} rows`}
            actions={
              <div className="actions">
                <button
                  className={show === 'all' ? 'small' : 'secondary small'}
                  onClick={() => setShow('all')}
                >
                  All
                </button>
                <button
                  className={show === 'errors' ? 'small' : 'secondary small'}
                  onClick={() => setShow('errors')}
                  disabled={detail.import.error_count === 0}
                >
                  Problems ({detail.import.error_count})
                </button>
              </div>
            }
          >
            {rows.length === 0 ? (
              <Empty>Nothing to show.</Empty>
            ) : (
              <table className="data">
                <thead>
                  <tr>
                    <th style={{ width: 48 }}>#</th>
                    <th>Membership</th>
                    <th>Who</th>
                    <th>Role</th>
                    <th>Class</th>
                    <th className="num">Balance</th>
                    <th>What is wrong with it</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.row_number} className={r.has_error ? 'neg' : undefined}>
                      <td className="muted">{r.row_number}</td>
                      <td>{r.membership_number ?? <span className="muted">—</span>}</td>
                      <td>
                        {r.name}
                        {r.age !== null && r.age < 18 && (
                          <span className="muted"> ({r.age})</span>
                        )}
                      </td>
                      <td className="muted small">
                        {r.role ? ROLE_LABEL[r.role] ?? r.role : '—'}
                      </td>
                      <td className="muted small">{r.class_code ?? ''}</td>
                      <td className="num">
                        {r.balance_minor === null ? '' : money(r.balance_minor, currency)}
                      </td>
                      <td className="small">
                        {r.errors.map((e, i) => (
                          <div key={i} className="neg">
                            {e}
                          </div>
                        ))}
                        {r.warnings.map((w, i) => (
                          <div key={i} className="muted">
                            {w}
                          </div>
                        ))}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            {detail.truncated && (
              <div className="hint">Showing the first 2000 rows.</div>
            )}
          </Card>

          {/* ------------------------------------------------------ */}
          <Card title="4 · Commit">
            {!atLeast('club_admin') ? (
              <p className="muted">
                Everything above can be done by the office. Committing creates the memberships and
                posts to the general ledger, so it needs the club's own administrator account.
              </p>
            ) : (
              <>
                {detail.can_commit ? (
                  <>
                    <p style={{ marginTop: 0 }}>
                      This creates{' '}
                      <strong>{detail.import.membership_count} memberships</strong> and{' '}
                      <strong>{detail.import.person_count} people</strong>, and posts{' '}
                      <strong>{money(detail.import.balance_minor, currency)}</strong> of opening
                      balances dated {fmtDate(detail.import.as_of_date)} — debit Accounts
                      Receivable, credit 3910 Opening Balances — Conversion.
                    </p>
                    <p className="muted small">
                      It happens in one go: either all of it or none of it. It cannot be undone by
                      pressing anything — unwinding a conversion means reversing the journal entry
                      and removing the memberships by hand.
                    </p>
                  </>
                ) : (
                  /* Stating what a commit *would* create, next to a button that
                     will refuse, reads as a promise the screen then breaks. */
                  <p style={{ marginTop: 0 }} className="muted">
                    Not yet.{' '}
                    {detail.import.error_count > 0
                      ? `${detail.import.error_count} row${detail.import.error_count === 1 ? '' : 's'} above still need attention.`
                      : "The file does not agree with the totals you gave in step 1."}
                  </p>
                )}

                <label className="row" style={{ marginTop: 12, marginBottom: 4, gap: 8 }}>
                  <input
                    type="checkbox"
                    style={{ width: 'auto', flex: '0 0 auto' }}
                    checked={confirmed}
                    onChange={(e) => setConfirmed(e.target.checked)}
                    disabled={!detail.can_commit}
                  />
                  <span>I have checked these totals against our own records.</span>
                </label>

                <div className="actions" style={{ marginTop: 10 }}>
                  <button
                    onClick={doCommit}
                    disabled={!detail.can_commit || !confirmed || commit.busy}
                  >
                    {commit.busy ? 'Importing…' : 'Import the members'}
                  </button>
                  {!detail.can_commit && (
                    <span className="muted small">
                      {detail.import.error_count > 0
                        ? 'Fix the rows above first.'
                        : "The file does not agree with your club's own totals."}
                    </span>
                  )}
                </div>
              </>
            )}
          </Card>
        </>
      )}

      {/* ---------------------------------------------------------- */}
      {!detail && (
        <Card title="Earlier Attempts">
          {history.loading ? (
            <Loading rows={2} />
          ) : (history.data?.imports ?? []).length === 0 ? (
            <Empty>Nothing has been imported yet.</Empty>
          ) : (
            <table className="data">
              <thead>
                <tr>
                  <th>File</th>
                  <th>When</th>
                  <th>As at</th>
                  <th className="num">Memberships</th>
                  <th className="num">Balances</th>
                  <th>State</th>
                </tr>
              </thead>
              <tbody>
                {(history.data?.imports ?? []).map((b) => (
                  <tr key={b.id}>
                    <td>{b.source_filename ?? <span className="muted">a file</span>}</td>
                    <td className="muted small">{fmtDate(b.created_at)}</td>
                    <td className="muted small">{fmtDate(b.as_of_date)}</td>
                    <td className="num">{b.membership_count}</td>
                    <td className="num">{money(b.balance_minor, currency)}</td>
                    <td>
                      <Status
                        tone={
                          b.status === 'committed'
                            ? 'good'
                            : b.status === 'rejected'
                              ? 'critical'
                              : 'neutral'
                        }
                      >
                        {b.status === 'validated' ? 'checked, not imported' : b.status}
                      </Status>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card>
      )}
    </>
  );
}

// ---------------------------------------------------------------------

/**
 * The club's own totals against the file's, side by side.
 *
 * Shown as a subtraction that has already been done. A screen that
 * prints two numbers and leaves the difference to the reader is a screen
 * where "412" and "312" look similar enough at four in the afternoon.
 */
function Reconcile({
  rec,
  batch,
  currency,
}: {
  rec: Reconciliation;
  batch: Batch;
  currency: Parameters<typeof money>[1];
}) {
  const countOff = rec.memberships_missing !== null && rec.memberships_missing !== 0;
  const moneyOff = rec.balance_difference_minor !== null && rec.balance_difference_minor !== 0;

  return (
    <>
      <div className="tiles">
        <div className="tile">
          <div className="tile__label">Memberships in the file</div>
          <div className="tile__value">{rec.memberships_found}</div>
          {rec.memberships_expected !== null && (
            <div className={countOff ? 'neg small' : 'muted small'}>
              {countOff
                ? rec.memberships_missing! > 0
                  ? `${rec.memberships_missing} missing — you said ${rec.memberships_expected}`
                  : `${-rec.memberships_missing!} more than the ${rec.memberships_expected} you said`
                : `agrees with your ${rec.memberships_expected}`}
            </div>
          )}
        </div>

        <div className="tile">
          <div className="tile__label">People</div>
          <div className="tile__value">{batch.person_count}</div>
          <div className="muted small">including spouses and dependents</div>
        </div>

        <div className="tile">
          <div className="tile__label">Opening balances</div>
          <div className="tile__value">{money(batch.balance_minor, currency)}</div>
          {rec.balance_expected_minor !== null && (
            <div className={moneyOff ? 'neg small' : 'muted small'}>
              {moneyOff
                ? `out by ${money(Math.abs(rec.balance_difference_minor!), currency)}`
                : 'agrees with your total'}
            </div>
          )}
        </div>

        <div className="tile">
          <div className="tile__label">Rows with problems</div>
          <div className={batch.error_count > 0 ? 'tile__value neg' : 'tile__value'}>
            {batch.error_count}
          </div>
          {batch.warning_count > 0 && (
            <div className="muted small">{batch.warning_count} worth a look</div>
          )}
        </div>
      </div>

      {countOff && (
        <div className="notice">
          <strong>This file does not match what you told us you have.</strong> Every row in it may
          be perfectly valid — an export that stopped early, or a spreadsheet saved with a filter
          on, looks exactly like this. Check the export before going any further.
        </div>
      )}
    </>
  );
}

/**
 * A refusal from the commit endpoint reads as a sentence about the
 * club's own file rather than as an error code, so it is given room
 * rather than being folded into the standard banner.
 */
function CommitError({ error }: { error: ApiError | null }) {
  if (!error) return null;
  return (
    <div className="notice" style={{ marginBottom: 16 }}>
      <strong>The import was refused.</strong>
      <div style={{ marginTop: 4 }}>{error.message}</div>
    </div>
  );
}
